import {Component, DestroyRef, inject, OnInit} from '@angular/core';
import {Aangifte, AangifteRapport, BokkzIbDataService, IbRekenService,} from '@snelstart-ventures/fiscaal/domain-logic';
import {FormControl, FormGroup} from '@angular/forms';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {CommonModule} from '@angular/common';
import {SharedUiAlertModule} from '@snelstart/ui-alert';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {BehaviorSubject} from "rxjs";

@Component({
    selector:
        'snelstart-venture-fiscaal-feature-box1-resultaat-overige-werkzaamheden',
    templateUrl: './box1-resultaat-overige-werkzaamheden.component.html',
    styleUrls: ['./box1-resultaat-overige-werkzaamheden.component.scss'],
    imports: [
        SharedUiDetailsModule,
        SharedUiFormControlsModule,
        CommonModule,
        SharedUiAlertModule
    ]
})
export class Box1ResultaatOverigeWerkzaamhedenComponent implements OnInit {

  private dataService = inject(BokkzIbDataService);
  private destroyRef = inject(DestroyRef);
  private bokkzClient = inject(IbRekenService);
  private rapportRequestId = 0;
  public dataVisible$ = new BehaviorSubject<boolean>(false);

  public specRowFormGroup = new FormGroup({
    berekendResultaatUitOverigeWerkzaamheden: new FormControl<number | null>(0),
    inkomstenUitOVerigeWerkzaamheden: new FormControl<number | null>(0),
    kostenTerZakeVanOverigeWerkzaamheden: new FormControl<number | null>(0),
    inkomstenUitTBS: new FormControl<number | null>(0),
    kostenTerZakeVanTBS: new FormControl<number | null>(0),
    tbsVrijstelling: new FormControl<number | null>(0),
    saldoRow: new FormControl<number | null>(0),
    saldoTbs: new FormControl<number | null>(0),
  });

  public specPuFormGroup = new FormGroup({
    berekendeUitkomst: new FormControl<number | null>(0),
    ontvangenAlimentatie: new FormControl<number | null>(0),
    kostenAlimentatie: new FormControl<number | null>(0),
    ontvangenPeriodiekeUitkeringen: new FormControl<number | null>(0),
    kostenPeriodiekeUitkeringen: new FormControl<number | null>(0),
    saldoAlimentatie: new FormControl<number | null>(0),
    saldoPerUitkeringen: new FormControl<number | null>(0),
  });

  ngOnInit(): void {
    this.dataService.partnerAangifte$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((isPartner) => {
      const aangifte = this.dataService.incomeTaxEntity.value.aangifte;
      if (!aangifte) {
        this.dataVisible$.next(false);
        return;
      }
      this.getRapportIfChanged(aangifte, isPartner);
    });
  }

  public getRapportIfChanged(aangifte: Aangifte, partner : boolean) {
    const requestId = ++this.rapportRequestId;
    this.dataVisible$.next(false);

    if (partner) {
      this.bokkzClient.getIncomeTaxReport(aangifte.partnerAangifte!).subscribe(rapport => {
        if (requestId !== this.rapportRequestId) {
          return;
        }
        this.resetAndPatchForms(rapport);
      })
    }
    else {
      this.bokkzClient.getIncomeTaxReport(aangifte).subscribe(rapport => {
        if (requestId !== this.rapportRequestId) {
          return;
        }
        this.resetAndPatchForms(rapport);
      });
    }
  }

  private resetAndPatchForms(details: AangifteRapport): void {
    this.specRowFormGroup.reset({}, {emitEvent: false});
    this.specPuFormGroup.reset({}, {emitEvent: false});
    this.specRowFormGroup.patchValue(details.specificatieROW?.opbouw ?? {}, {emitEvent: false});
    this.specRowFormGroup.patchValue({saldoRow: this.calculateSaldoRow()}, {emitEvent: false});
    this.specRowFormGroup.patchValue({saldoTbs: this.calculateSaldoTbs()}, {emitEvent: false});
    this.specPuFormGroup.patchValue(details.specificatiePU?.opbouw ?? {}, {emitEvent: false});
    this.specPuFormGroup.patchValue({saldoAlimentatie: this.calculateSaldoAlimentatie()}, {emitEvent: false});
    this.specPuFormGroup.patchValue({saldoPerUitkeringen: this.calculateSaldoPu()}, {emitEvent: false});
    this.specRowFormGroup.disable({emitEvent: false});
    this.specPuFormGroup.disable({emitEvent: false});
    this.dataVisible$.next(true);
  }

  calculateSaldoRow(): any {
    const plusRaw = this.specRowFormGroup.get('inkomstenUitOVerigeWerkzaamheden')?.value;
    const minRaw = this.specRowFormGroup.get('kostenTerZakeVanOverigeWerkzaamheden')?.value;

    if (plusRaw == null && minRaw == null) {
      const fallback = this.specRowFormGroup.get('saldoRow')?.value
        ?? this.specRowFormGroup.get('berekendResultaatUitOverigeWerkzaamheden')?.value
        ?? 0;
      this.specRowFormGroup.patchValue({saldoRow: fallback}, {emitEvent: false});
      return fallback;
    }

    const plus = plusRaw ?? 0;
    const min = minRaw ?? 0;
    const sum = plus - min;
    this.specRowFormGroup.patchValue({saldoRow: sum}, {emitEvent: false});
    return sum;
  }

  calculateSaldoTbs(): any {
    const plusRaw = this.specRowFormGroup.get('inkomstenUitTBS')?.value;
    const minRaw = this.specRowFormGroup.get('kostenTerZakeVanTBS')?.value;
    const vrijRaw = this.specRowFormGroup.get('tbsVrijstelling')?.value;

    if (plusRaw == null && minRaw == null && vrijRaw == null) {
      const fallback = this.specRowFormGroup.get('saldoTbs')?.value ?? 0;
      this.specRowFormGroup.patchValue({saldoTbs: fallback}, {emitEvent: false});
      return fallback;
    }

    const plus = plusRaw ?? 0;
    const min = minRaw ?? 0;
    const vrij = vrijRaw ?? 0;
    const sum = plus - min - vrij;
    this.specRowFormGroup.patchValue({saldoTbs: sum}, {emitEvent: false});
    return sum;
  }

  calculateSaldoAlimentatie(): any {
    const plusRaw = this.specPuFormGroup.get('ontvangenAlimentatie')?.value;
    const minRaw = this.specPuFormGroup.get('kostenAlimentatie')?.value;

    if (plusRaw == null && minRaw == null) {
      const fallback = this.specPuFormGroup.get('saldoAlimentatie')?.value ?? 0;
      this.specPuFormGroup.patchValue({saldoAlimentatie: fallback}, {emitEvent: false});
      return fallback;
    }

    const plus = plusRaw ?? 0;
    const min = minRaw ?? 0;
    const sum = plus - min;
    this.specPuFormGroup.patchValue({saldoAlimentatie: sum}, {emitEvent: false});
    return sum;
  }

  calculateSaldoPu(): any {
    const plusRaw = this.specPuFormGroup.get('ontvangenPeriodiekeUitkeringen')?.value;
    const minRaw = this.specPuFormGroup.get('kostenPeriodiekeUitkeringen')?.value;

    if (plusRaw == null && minRaw == null) {
      const fallback = this.specPuFormGroup.get('saldoPerUitkeringen')?.value
        ?? 0;
      this.specPuFormGroup.patchValue({saldoPerUitkeringen: fallback}, {emitEvent: false});
      return fallback;
    }

    const plus = plusRaw ?? 0;
    const min = minRaw ?? 0;
    const sum = plus - min;
    this.specPuFormGroup.patchValue({saldoPerUitkeringen: sum}, {emitEvent: false});
    return sum;
  }

  get heeftGeenOverigeInkomsten(): boolean {
    return (this.specRowFormGroup.controls.saldoRow.value ?? 0) === 0
      && (this.specRowFormGroup.controls.saldoTbs.value ?? 0) === 0
      && (this.specPuFormGroup.controls.saldoAlimentatie.value ?? 0) === 0
      && (this.specPuFormGroup.controls.saldoPerUitkeringen.value ?? 0) === 0;
  }
}
